import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { API_ENDPOINTS, METHOD_TYPE } from "../../utils/apiUrls";
import api from "../../utils/api";

const initialState = {
    isLoading: false,
    error: null,
    individualScreeningList: { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 },
    individualScreeningClientList: { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 },
    pendingKYCindividualScreeningList: { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 },
    entityClientBanks: [],
    entityClientBankDetails: {},
    entityClientMortgages: [],
    entityClientCompany: [],
    entityClientCompanyDetails: {},
    fetchDocuments: { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 },
    kycDocuments: [],
    adverseMedias: [],
    screeningComments: { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 },
    fieldHistory: { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 },
    // Audit trail Reasons tab: Reasons comments (e.g. IDV decisions) + field changes.
    auditReasons: { rows: [], count: 0, totalPages: 0, currentPage: 1 },
    globalSearchDetail: null,
    globalSearches: { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 },
    oldData: [],
    amlComments: { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 },
    amlCardComments: { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 },
    applications: { rows: [] },
    corporateApplications: { rows: [] },
    searchCertificateDetails: null,
    entityCertificateDetails: null
}


export const fetchIndividualScreeningList = createAsyncThunk("individual_screening/getIndividualScreeninglist", async (queryParam) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getIndividualScreeninglist + queryParam
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchClientIndividualScreeninglist = createAsyncThunk("individual_screening/getClientIndividualScreeninglist", async (queryParam) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getClientIndividualScreeninglist + queryParam
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const listPendingKYCIndividualScreening = createAsyncThunk("individual_screening/listPendingKYCIndividualScreening", async (queryParam) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.listPendingKYCIndividualScreening + queryParam
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const updateIndividualPersonalDetailsWithReason = createAsyncThunk("individual_screening/updateIndividualPersonalDetailsWithReason", async ({ payload, queryParam }, { rejectWithValue }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.updateIndividualPersonalDetailsWithReason + queryParam,
            data: payload
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        return rejectWithValue(error?.response?.data || { message: error?.message || 'Unable to update the record' });
    }
});

export const updateKycOcrMetadataAction = createAsyncThunk("individual_screening/updateKycOcrMetadata", async ({ payload }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.updateKycOcrMetadata,
            data: payload
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const addScreeningComments = createAsyncThunk("individual_screening/addScreeningComments", async ({ payload }) => {
    try {
        const normalizedPayload = {
            ...payload,
            entityClientId: payload?.entityClientId ?? payload?.entity_client_id,
            entity_client_id: payload?.entity_client_id ?? payload?.entityClientId,
            globalSearchResultId: payload?.globalSearchResultId ?? payload?.global_search_results_id ?? payload?.global_search_result_id,
            global_search_results_id: payload?.global_search_results_id ?? payload?.global_search_result_id ?? payload?.globalSearchResultId,
            comment_type: payload?.comment_type || payload?.commentType || 'aml',
            commentType: payload?.commentType || payload?.comment_type || 'aml'
        }

        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.addScreeningComments,
            data: normalizedPayload
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const getEntityClientBank = createAsyncThunk("individual_screening/getEntityClientBank", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getEntityClientBank + queryParams
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const getEntityClientBankDetails = createAsyncThunk("individual_screening/getEntityClientBankDetails", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getEntityClientBankDetails + queryParams
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const getEntityClientMortgages = createAsyncThunk("individual_screening/getEntityClientMortgages", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getEntityClientMortgages + queryParams
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const addEntityClientMortgage = createAsyncThunk("individual_screening/addEntityClientMortgage", async ({ payload, queryParams }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.addEntityClientMortgage + (queryParams || ''),
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const updateEntityClientMortgage = createAsyncThunk("individual_screening/updateEntityClientMortgage", async ({ payload, queryParams }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.updateEntityClientMortgage + (queryParams || ''),
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const uploadMortgageDocument = createAsyncThunk("individual_screening/uploadMortgageDocument", async (formData) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.uploadMortgageDocument,
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' }
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const getEntityClientCompanyDetails = createAsyncThunk("individual_screening/getEntityClientCompanyDetails", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getEntityClientCompanyDetails + queryParams
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const listKycDocuments = createAsyncThunk("individual_screening/listKycDocuments", async (request) => {
    // Callers pass the query string; the customer web form also passes its link token.
    const queryParams = typeof request === 'string' ? request : (request?.queryParams || '');
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.listKycDocuments + queryParams,
            ...(request?.webformToken ? { headers: { publictoken: request.webformToken } } : {}),
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const requestDocument = createAsyncThunk("individual_screening/requestDocument", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.requestDocument + queryParams
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const uploadDocument = createAsyncThunk("individual_screening/uploadDocument", async (payload) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.uploadDocument,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchUploadedDocuments = createAsyncThunk("individual_screening/fetchUploadedDocuments", async (queryParam) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.fetchUploadedDocuments + queryParam
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const updateScreeningInfo = createAsyncThunk("individual_screening/updateScreeningInfo", async ({ payload, queryParam }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.updateScreeningInfo + queryParam,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const getScreeningComments = createAsyncThunk("individual_screening/getScreeningComments", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getScreeningComments + queryParams
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const getFieldHistory = createAsyncThunk("individual_screening/getFieldHistory", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getFieldHistory + queryParams
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

const auditTimestamp = (row = {}) => new Date(row?.created_at || row?.updated_at || 0).getTime() || 0;

// One Reasons list, newest first: comments filed as Reasons (IDV decisions, KYC mismatch
// reviews) and recorded field changes. Both sources are paged by the API with the same
// page / limit, so a page shows that page of each and the total is the larger of the two.
export const mergeAuditReasons = (comments = {}, fieldHistory = {}) => {
    const commentRows = (comments?.rows || []).map((row) => ({ ...row, audit_kind: 'comment' }));
    const historyRows = (fieldHistory?.rows || []).map((row) => ({ ...row, audit_kind: 'field_change' }));
    return {
        rows: [...commentRows, ...historyRows].sort((a, b) => auditTimestamp(b) - auditTimestamp(a)),
        count: Number(comments?.count || 0) + Number(fieldHistory?.count || 0),
        totalPages: Math.max(Number(comments?.totalPages || 0), Number(fieldHistory?.totalPages || 0)),
        currentPage: Number(comments?.currentPage || fieldHistory?.currentPage || 1),
    };
};

export const getAuditReasons = createAsyncThunk("individual_screening/getAuditReasons", async ({ entityClientId, page = 1, limit = 10 }) => {
    // One source failing (e.g. field history needs the AML permission) must not hide the other.
    const [comments, fieldHistory] = await Promise.allSettled([
        api({ method: METHOD_TYPE.get, url: `${API_ENDPOINTS.getScreeningComments}?entity_client_id=${entityClientId}&type=reasons&page=${page}&limit=${limit}` }),
        api({ method: METHOD_TYPE.get, url: `${API_ENDPOINTS.getFieldHistory}?entity_client_id=${entityClientId}&page=${page}&limit=${limit}` }),
    ]);
    if (comments.status === 'rejected' && fieldHistory.status === 'rejected') {
        throw comments.reason?.response?.data || { message: 'Unable to load Reasons' };
    }
    return {
        data: mergeAuditReasons(
            comments.status === 'fulfilled' ? comments.value?.data?.data : {},
            fieldHistory.status === 'fulfilled' ? fieldHistory.value?.data?.data : {},
        ),
    };
});

export const getGlobalSearchById = createAsyncThunk("individual_screening/getGlobalSearchById", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getGlobalSearchById + queryParams
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const getAllGlobalSearchById = createAsyncThunk("individual_screening/getAllGlobalSearchById", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getAllGlobalSearchById + queryParams
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});
export const getIndividualScreeningClientHistory = createAsyncThunk("individual_screening/getIndividualScreeningClientHistory", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getIndividualScreeningClientHistory + queryParams
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});
export const saveRelevantResult = createAsyncThunk("individual_screening/saveRelevantResult", async ({ payload }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.saveRelevantResult,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const removeRelevantResult = createAsyncThunk("individual_screening/removeRelevantResult", async ({ payload }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.removeRelevantResult,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const getSearchCertificate = createAsyncThunk("individual_screening/getSearchCertificate", async ({ queryParams, payload }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.getSearchCertificate + queryParams,
            data: payload
            // responseType: 'arraybuffer'
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});
export const getAmlCheckSearchCertificate = createAsyncThunk("individual_screening/getAmlCheckSearchCertificate", async ({ queryParams, payload }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.getAmlCheckSearchCertificate + queryParams,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});
export const getAdverseMedia = createAsyncThunk("individual_screening/getAdverseMedia", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getAdverseMedia + queryParams
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const manageContinuousMonitoring = createAsyncThunk("individual_screening/manageContinuousMonitoring", async ({ queryParams, payload }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.manageContinuousMonitoring + queryParams,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const saveExcludedAdverseMedia = createAsyncThunk("individual_screening/saveExcludedAdverseMedia", async (payload) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.saveExcludedAdverseMedia,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
})
export const manageScreeningAction = createAsyncThunk("individual_screening/manageScreeningAction", async ({ queryParams, payload }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.manageScreeningAction + queryParams,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
})

export const manageEntityClientStatus = createAsyncThunk("individual_screening/manageEntityClientStatus", async ({ queryParams, payload }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.manageEntityClientStatus + queryParams,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
})
export const saveAmlInfoComment = createAsyncThunk("individual_screening/saveAmlInfoComment", async (payload) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.saveAmlInfoComment,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
})
export const saveAdverseMediaComment = createAsyncThunk("individual_screening/saveAdverseMediaComment", async (payload) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.saveAdverseMediaComment,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
})
export const downloadClientDocZipFiles = createAsyncThunk("individual_screening/downloadClientDocZipFiles", async (queryParam) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.downloadClientDocZipFiles + queryParam,
            responseType: 'blob',
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        if (error.response && error.response.data instanceof Blob) {
            const errorText = await error.response.data.text();
            try {
                const errorJson = JSON.parse(errorText);
                throw new Error(errorJson?.message || 'Unable to download the ZIP file');
            } catch (parseError) {
                if (parseError instanceof SyntaxError) {
                    throw new Error(errorText || 'Unable to download the ZIP file');
                }
                throw parseError;
            }
        }
        throw new Error(error?.response?.data?.message || error?.message || 'Unable to download the ZIP file');
    }
});

export const resendToCheckerMaker = createAsyncThunk("individual_screening/resendToCheckerMaker", async ({ payload, queryParam }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.resendToCheckerMaker + queryParam,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const sendToCheckerMlro = createAsyncThunk("individual_screening/sendToCheckerMlro", async ({ payload, queryParam }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.sendToCheckerMlro + queryParam,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchAmlComments = createAsyncThunk("individual_screening/fetchAmlComments", async (queryParam) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.fetchAmlComments + queryParam,
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
})

export const fetchAmlCheckCommentsAction = createAsyncThunk("aml_check/fetchAmlCheckComments", async (queryParam) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.fetchAmlCheckComments + queryParam,
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
})

export const saveAmlCheckCommentAction = createAsyncThunk("aml_check/saveAmlCheckComment", async (payload) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.saveAmlCheckComment,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
})

export const fetchAmlCardComments = createAsyncThunk("individual_screening/fetchAmlCardComments", async (queryParam) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.fetchAmlComments + queryParam,
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
})


export const uploadMediaImage = createAsyncThunk("individual_screening/uploadMediaImage", async ({ queryParams, payload }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.uploadMediaImage + queryParams,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
})

export const getEntityCertificate = createAsyncThunk("individual_screening/getEntityCertificate", async ({ queryParams, payload }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.getEntityCertificate + queryParams,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const getAmlCheckEntityCertificate = createAsyncThunk("individual_screening/getAmlCheckEntityCertificate", async ({ queryParams, payload }) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.getAmlCheckEntityCertificate + queryParams,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const duplicateApproveReject = createAsyncThunk("individual_screening/duplicateApproveReject", async (payload) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.duplicateApproveReject,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const getOnboardingApplications = createAsyncThunk("individual_screening/getOnboardingApplications", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getOnboardingApplications + queryParams
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const resendOnboardingInviteLink = createAsyncThunk("individual_screening/resendOnboardingInviteLink", async (payload) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.resendOnboardingInviteLink,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const getCorporateOnboardingApplications = createAsyncThunk("individual_screening/getCorporateOnboardingApplications", async (queryParams) => {
    try {
        const response = await api({
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getCorporateOnboardingApplications + queryParams,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const resendCorporateOnboardingInviteLink = createAsyncThunk("individual_screening/resendCorporateOnboardingInviteLink", async (payload) => {
    try {
        const response = await api({
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.resendCorporateOnboardingInviteLink,
            data: payload,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const getInactiveOomeroApplications = createAsyncThunk("individual_screening/getInactiveOomeroApplications", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getInactiveOomeroApplications + queryParams
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const resendFaceKycLink = createAsyncThunk("individual_screening/resendFaceKycLink", async (payload) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.resendFaceKycLink,
            data: payload
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const generateOnboardingInviteLink = createAsyncThunk("individual_screening/generateOnboardingInviteLink", async (payload) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.generateOnboardingInviteLink,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});


const fetchIndividualScreeningSlice = createSlice({
    name: "individualScreening",
    initialState,
    reducers: {
        resetIndividualScreeningList(state, action) {
            state.individualScreeningList = { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 };
        },
        resetClientIndividualScreeningList(state, action) {
            state.individualScreeningClientList = { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 };
        },
        resetListPendingKYCIndividualScreening(state, action) {
            state.pendingKYCindividualScreeningList = { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 };
        },
        resetGlobalSearchDetails(state, action) {
            state.globalSearchDetail = null;
        },
        updateIndividualScreeningList(state, action) {
            state.individualScreeningList = action.payload;
        },
        updateListPendingKYCIndividualScreening(state, action) {
            state.pendingKYCindividualScreeningList = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchIndividualScreeningList.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.individualScreeningList = payload.data;
            })
            .addCase(fetchClientIndividualScreeninglist.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.individualScreeningClientList = payload.data;
            })
            .addCase(listPendingKYCIndividualScreening.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.pendingKYCindividualScreeningList = payload.data;
            })
            .addCase(updateIndividualPersonalDetailsWithReason.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(addScreeningComments.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(getEntityClientBank.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.entityClientBanks = payload.data
            })
            .addCase(getEntityClientBankDetails.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.entityClientBankDetails = payload.data
            })
            .addCase(getEntityClientMortgages.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.entityClientMortgages = payload?.data || [];
            })
            .addCase(getEntityClientCompanyDetails.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.entityClientCompanyDetails = payload.data
            })
            .addCase(listKycDocuments.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.kycDocuments = payload.data;
            })
            .addCase(requestDocument.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(uploadDocument.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(fetchUploadedDocuments.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.fetchDocuments = payload.data
            })
            .addCase(updateScreeningInfo.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(getScreeningComments.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.screeningComments = payload.data;
            })
            .addCase(getFieldHistory.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.fieldHistory = payload.data;
            })
            .addCase(getAuditReasons.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.auditReasons = payload.data;
            })
            .addCase(getGlobalSearchById.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.globalSearchDetail = payload.data;
            })
            .addCase(getAllGlobalSearchById.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.globalSearches = payload.data;
            })
            .addCase(getIndividualScreeningClientHistory.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.oldData = payload.data;
            })
            .addCase(saveRelevantResult.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(removeRelevantResult.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(getSearchCertificate.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.searchCertificateDetails = payload.data;
                state.error = null;
            })
            .addCase(getAmlCheckSearchCertificate.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.searchCertificateDetails = payload.data;
                state.error = null;
            })
            .addCase(getEntityCertificate.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.entityCertificateDetails = payload.data;
                state.error = null;
            })
            .addCase(getAmlCheckEntityCertificate.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.entityCertificateDetails = payload.data;
                state.error = null;
            })
            .addCase(manageContinuousMonitoring.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(getAdverseMedia.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.adverseMedias = payload.data
                state.error = null;
            })
            .addCase(manageScreeningAction.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(manageEntityClientStatus.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(saveExcludedAdverseMedia.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(saveAmlInfoComment.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(saveAdverseMediaComment.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(downloadClientDocZipFiles.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(fetchAmlCheckCommentsAction.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.amlComments = payload.data;
            })
            .addCase(saveAmlCheckCommentAction.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(resendToCheckerMaker.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(sendToCheckerMlro.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(uploadMediaImage.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(resendOnboardingInviteLink.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(resendCorporateOnboardingInviteLink.fulfilled, (state) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(resendFaceKycLink.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(generateOnboardingInviteLink.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(fetchAmlComments.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.amlComments = payload.data
            })
            .addCase(fetchAmlCardComments.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.amlCardComments = payload.data
            })
            .addCase(duplicateApproveReject.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(getOnboardingApplications.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.applications = {
                    rows: payload?.data?.data || [],
                    currentPage: payload?.data?.pagination?.currentPage || 1,
                    totalPages: payload?.data?.pagination?.totalPages || 1,
                    totalRecords: payload?.data?.pagination?.totalRecords || 0,
                }
                state.error = null;
            })
            .addCase(getCorporateOnboardingApplications.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                const responseData = payload?.data;
                const pagination = responseData?.pagination || payload?.pagination || {};
                state.corporateApplications = {
                    rows: Array.isArray(responseData) ? responseData : (responseData?.data || responseData?.rows || []),
                    currentPage: pagination.currentPage || responseData?.currentPage || 1,
                    totalPages: pagination.totalPages || responseData?.totalPages || 1,
                    totalRecords: pagination.totalRecords || responseData?.totalRecords || responseData?.count || 0,
                };
                state.error = null;
            })
            .addCase(getInactiveOomeroApplications.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.applications = payload.data;
                state.error = null;
            })
            .addMatcher(isAnyOf(fetchIndividualScreeningList.pending, listPendingKYCIndividualScreening.pending, updateIndividualPersonalDetailsWithReason.pending, addScreeningComments.pending, getEntityClientBank.pending, getEntityClientBankDetails.pending, getEntityClientCompanyDetails.pending, listKycDocuments.pending, requestDocument.pending, uploadDocument.pending, fetchUploadedDocuments.pending, updateScreeningInfo.pending, getScreeningComments.pending, getFieldHistory.pending, getAuditReasons.pending, getGlobalSearchById.pending, getAllGlobalSearchById.pending, getIndividualScreeningClientHistory.pending, saveRelevantResult.pending, removeRelevantResult.pending, getSearchCertificate.pending, manageContinuousMonitoring.pending, getAdverseMedia.pending, manageScreeningAction.pending, saveExcludedAdverseMedia.pending, manageEntityClientStatus.pending, saveAmlInfoComment.pending, saveAdverseMediaComment.pending, downloadClientDocZipFiles.pending, resendToCheckerMaker.pending, fetchAmlComments.pending, fetchAmlCardComments.pending, uploadMediaImage.pending, sendToCheckerMlro.pending, getEntityCertificate.pending, getAmlCheckSearchCertificate.pending, getAmlCheckEntityCertificate.pending, fetchClientIndividualScreeninglist.pending, duplicateApproveReject.pending, getOnboardingApplications.pending, getCorporateOnboardingApplications.pending, resendOnboardingInviteLink.pending, resendCorporateOnboardingInviteLink.pending, getInactiveOomeroApplications.pending, resendFaceKycLink.pending, generateOnboardingInviteLink.pending), (state) => {
                state.isLoading = true;
            })
            .addMatcher(isAnyOf(fetchIndividualScreeningList.rejected, listPendingKYCIndividualScreening.rejected, updateIndividualPersonalDetailsWithReason.rejected, addScreeningComments.rejected, getEntityClientBank.rejected, getEntityClientBankDetails.rejected, getEntityClientCompanyDetails.rejected, listKycDocuments.rejected, requestDocument.rejected, uploadDocument.rejected, fetchUploadedDocuments.rejected, updateScreeningInfo.rejected, getScreeningComments.rejected, getFieldHistory.rejected, getAuditReasons.rejected, getGlobalSearchById.rejected, getAllGlobalSearchById.rejected, getIndividualScreeningClientHistory.rejected, saveRelevantResult.rejected, removeRelevantResult.rejected, getSearchCertificate.rejected, manageContinuousMonitoring.rejected, getAdverseMedia.rejected, saveExcludedAdverseMedia.rejected, manageScreeningAction.rejected, manageEntityClientStatus.rejected, saveAmlInfoComment.rejected, saveAdverseMediaComment.rejected, downloadClientDocZipFiles.rejected, resendToCheckerMaker.rejected, fetchAmlComments.rejected, fetchAmlCardComments.rejected, uploadMediaImage.rejected, sendToCheckerMlro.rejected, getEntityCertificate.rejected, getAmlCheckSearchCertificate.rejected, getAmlCheckEntityCertificate.rejected, fetchClientIndividualScreeninglist.rejected, duplicateApproveReject.rejected, getOnboardingApplications.rejected, getCorporateOnboardingApplications.rejected, resendOnboardingInviteLink.rejected, resendCorporateOnboardingInviteLink.rejected, getInactiveOomeroApplications.rejected, resendFaceKycLink.rejected, generateOnboardingInviteLink.rejected), (state, { error }) => {
                state.isLoading = false;
                state.error = (error) ? error.message : "Request Failed Please Try Again ";
            })
    }
});

const { resetIndividualScreeningList, resetGlobalSearchDetails, resetListPendingKYCIndividualScreening, updateIndividualScreeningList, updateListPendingKYCIndividualScreening, resetClientIndividualScreeningList } = fetchIndividualScreeningSlice.actions;
const individualScreeningReducer = fetchIndividualScreeningSlice.reducer;

export { resetIndividualScreeningList, resetGlobalSearchDetails, resetListPendingKYCIndividualScreening, updateIndividualScreeningList, updateListPendingKYCIndividualScreening, resetClientIndividualScreeningList, individualScreeningReducer };
export default individualScreeningReducer;
